import { useCallback, useRef, useEffect } from 'react';

interface UseDebouncedHoverOptions<T = void> {
  onShow?: (data?: T) => void;
  onHide?: () => void;
  delay?: number;
}

interface UseDebouncedHoverReturn<T = void> {
  handleMouseEnter: (data?: T) => void;
  handleMouseLeave: () => void;
  cancel: () => void;
}

const DEFAULT_HOVER_DELAY = 300;

export function useDebouncedHover(options: UseDebouncedHoverOptions): {
  handleMouseEnter: () => void;
  handleMouseLeave: () => void;
  cancel: () => void;
};

export function useDebouncedHover<T>(
  options: UseDebouncedHoverOptions<T>,
): UseDebouncedHoverReturn<T>;

export function useDebouncedHover<T = void>({
  onShow,
  onHide,
  delay = DEFAULT_HOVER_DELAY,
}: UseDebouncedHoverOptions<T>): UseDebouncedHoverReturn<T> {
  const hideTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const cancel = useCallback(() => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const handleMouseEnter = useCallback(
    (data?: T) => {
      // Cancel any pending hide
      cancel();
      onShow?.(data);
    },
    [cancel, onShow],
  );

  const handleMouseLeave = useCallback(() => {
    // Don't hide immediately - give user time to move mouse to popover
    hideTimeoutRef.current = setTimeout(() => {
      onHide?.();
      hideTimeoutRef.current = null;
    }, delay);
  }, [onHide, delay]);

  useEffect(() => {
    return () => {
      cancel();
    };
  }, [cancel]);

  return {
    handleMouseEnter,
    handleMouseLeave,
    cancel,
  };
}
